import { useState } from "react";
import { Alert } from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
} from "@volca/ui/components/ui/empty";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { useApi } from "@/core/api";
import { extractErrorMessage } from "@/core/api/errors";
import { dayjs } from "@/core/utils/date";
import { formatFileSize } from "./document-utils";
import { invoiceSampleDownloadUrl } from "./onboarding-api";
import { INVOICE_SAMPLE_SOURCE, type InvoiceSample } from "./types";

export const InvoiceSampleList = ({
  samples,
}: {
  samples: InvoiceSample[];
}) => {
  const api = useApi();
  const [downloading, setDownloading] = useState<string>();
  const [error, setError] = useState<string>();
  const download = async (sample: InvoiceSample) => {
    setDownloading(sample.id);
    setError(undefined);
    try {
      const path = invoiceSampleDownloadUrl(sample.url, api.defaults.baseURL!);
      const { data } = await api.get<Blob>(path, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = sample.filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (failure) {
      setError(
        `Could not download ${sample.filename}. ${extractErrorMessage(failure).message}`,
      );
    } finally {
      setDownloading(undefined);
    }
  };
  return (
    <Stack className="w-full" gap={2} align="stretch">
      {error && <Alert color="danger">{error}</Alert>}
      {!samples.length && (
        <Empty className={emptyStateVariants({ layout: "inline" })}>
          <EmptyHeader>
            <EmptyDescription>No invoice samples yet.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      )}
      {samples.map((sample) => (
        <Stack
          key={sample.id}
          direction="row"
          gap={4}
          align="center"
          className="w-full border-b border-line py-3 last:border-b-0"
        >
          <Stack gap={1} align="stretch" className="min-w-0 flex-1">
            <Text className="truncate" title={sample.filename}>
              {sample.filename}
            </Text>
            {/* Samples can arrive two ways now, and a customer who emailed forty of
                them should be able to tell those apart from the ones they dropped in. */}
            <Text variant="caption" tone="muted">
              {formatFileSize(sample.byte_size)} ·{" "}
              {dayjs(sample.created_at).format("MMM D, YYYY")}
              {sample.source === INVOICE_SAMPLE_SOURCE.email &&
                " · Sent by email"}
            </Text>
          </Stack>
          <Button
            variant="link"
            onClick={() => void download(sample)}
            aria-label={`Download ${sample.filename}`}
            disabled={downloading !== undefined}
            aria-busy={downloading === sample.id || undefined}
          >
            {downloading === sample.id && (
              <Spinner aria-hidden data-icon="inline-start" />
            )}
            Download
          </Button>
        </Stack>
      ))}
    </Stack>
  );
};
